import React, { useState, useEffect, useCallback } from "react";
import {
  Shield, Key, AlertTriangle, CheckCircle, RefreshCw, Settings,
  Github, ExternalLink, Loader, Info,
  ChevronDown, ChevronRight, TrendingUp, Zap,
  AlertCircle, Wrench, User, Clock
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import Card from "../components/Card";
import Button from "../components/Buttons";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";

const SEVERITY_COLORS = {
  Critical: { badge: "bg-red-100 text-red-700 border border-red-200" },
  High:     { badge: "bg-red-50 text-red-600 border border-red-200" },
  Medium:   { badge: "bg-orange-50 text-orange-600 border border-orange-200" },
  Low:      { badge: "bg-yellow-50 text-yellow-700 border border-yellow-200" },
};

const RISK_RATING_STYLE = {
  CRITICAL: { pill: "bg-red-100 text-red-700 border border-red-200",        accent: "border-l-red-500" },
  HIGH:     { pill: "bg-orange-50 text-orange-700 border border-orange-200", accent: "border-l-orange-500" },
  MEDIUM:   { pill: "bg-yellow-50 text-yellow-700 border border-yellow-200", accent: "border-l-yellow-400" },
  LOW:      { pill: "bg-green-50 text-green-700 border border-green-200",    accent: "border-l-green-500" },
};

const RISK_STATUS_LABEL = {
  identified:  "Identified",
  in_progress: "In Progress",
  closed:      "Closed",
  accepted:    "Accepted",
};

const STATUS_OPTIONS = ["Open", "Acknowledged", "FalsePositive", "Resolved"];
const STATUS_LABEL = {
  Open: "Open",
  Acknowledged: "Acknowledged",
  FalsePositive: "False Positive",
  Resolved: "Resolved",
};

const REPO_PROVIDER_BADGE = {
  GitHub: "bg-gray-800",
  GitLab: "bg-orange-500",
  "Azure DevOps": "bg-blue-600",
};

// Map a repo URL to its hosting provider label. Falls back to null when there is no URL.
function repoProviderFor(repoUrl) {
  if (!repoUrl) return null;
  let host = "";
  try {
    host = new URL(repoUrl).hostname.toLowerCase();
  } catch {
    return null;
  }
  if (host === "github.com" || host.endsWith(".github.com")) return "GitHub";
  if (host === "dev.azure.com" || host.endsWith(".visualstudio.com")) return "Azure DevOps";
  if (host === "gitlab.com" || host.endsWith(".gitlab.com")) return "GitLab";
  // Self-hosted / unknown host — surface the host so the label is never misleading.
  return host;
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const secs = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (secs < 60)   return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  const days = Math.floor(secs / 86400);
  if (days === 1)  return "yesterday";
  if (days < 30)   return `${days} days ago`;
  if (days < 365)  return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function isToday(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const n = new Date();
  return d.getDate() === n.getDate() && d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
}

export default function SecretsScanningDashboard() {
  const apiCall = useApi();
  const { canManageOrganizationRisks } = useRBAC();
  const org = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const orgId = org?.organizationId;
  const isAdmin = canManageOrganizationRisks(orgId);

  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [findings, setFindings] = useState([]);
  const [risksByFindingId, setRisksByFindingId] = useState({});
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanTimedOut, setScanTimedOut] = useState(false);

  const [filterSeverity, setFilterSeverity] = useState("");
  const [filterStatus, setFilterStatus] = useState("Open");
  const [filterSecretType, setFilterSecretType] = useState("");

  function buildQuery() {
    const params = new URLSearchParams();
    if (filterSeverity) params.append("severity", filterSeverity);
    if (filterStatus) params.append("status", filterStatus);
    if (filterSecretType) params.append("secretType", filterSecretType);
    return params.toString();
  }

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, sum, fnd, rsk] = await Promise.all([
        apiCall("GET", "/secrets-scan/config").catch(() => null),
        apiCall("GET", "/secrets-scan/summary").catch(() => null),
        apiCall("GET", `/secrets-scan/findings?${buildQuery()}`).catch(() => []),
        apiCall("GET", "/secrets-scan/risks").catch(() => []),
      ]);
      setConfig(cfg);
      setSummary(sum);
      setFindings(Array.isArray(fnd) ? fnd : []);
      const byId = {};
      if (Array.isArray(rsk)) {
        rsk.forEach(r => { if (r.secretsScanFindingId != null) byId[r.secretsScanFindingId] = r; });
      }
      setRisksByFindingId(byId);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSeverity, filterStatus, filterSecretType]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleTriggerScan() {
    setScanning(true);
    setScanTimedOut(false);
    const scanStarted = new Date();
    try {
      await apiCall("POST", "/secrets-scan/scan");
      // Poll every 5 s for up to 60 s (12 attempts).
      // Stops as soon as lastScannedAt advances past the click time.
      // After 60 s auto-reloads once and hands control back via the Refresh button.
      let attempts = 0;
      const maxAttempts = 12;
      const poll = async () => {
        attempts++;
        try {
          const sum = await apiCall("GET", "/secrets-scan/summary").catch(() => null);
          if (sum?.lastScannedAt && new Date(sum.lastScannedAt) > scanStarted) {
            await loadData();
            setScanning(false);
            setScanTimedOut(false);
            return;
          }
        } catch { /* keep polling */ }
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        } else {
          await loadData();
          setScanning(false);
          setScanTimedOut(true);
        }
      };
      setTimeout(poll, 5000);
    } catch {
      setScanning(false);
    }
  }

  function handleDismissScan() {
    setScanning(false);
    setScanTimedOut(false);
    loadData();
  }

  async function handleStatusChange(findingId, newStatus) {
    try {
      await apiCall("PATCH", `/secrets-scan/findings/${findingId}/status`, { status: newStatus });
      setFindings(prev => prev.map(f => f.id === findingId ? { ...f, status: newStatus } : f));
      const [updatedSummary, updatedRisks] = await Promise.all([
        apiCall("GET", "/secrets-scan/summary").catch(() => null),
        apiCall("GET", "/secrets-scan/risks").catch(() => []),
      ]);
      if (updatedSummary) setSummary(updatedSummary);
      if (Array.isArray(updatedRisks)) {
        const byId = {};
        updatedRisks.forEach(r => { if (r.secretsScanFindingId != null) byId[r.secretsScanFindingId] = r; });
        setRisksByFindingId(byId);
      }
    } catch {
      alert("Failed to update status.");
    }
  }

  const secretTypes = [...new Set(findings.map(f => f.secretType))].sort();

  if (loading) {
    return (
      <div className="min-h-screen bg-theme-bg">
        <Navigation />
        <div className="flex items-center justify-center py-24">
          <Loader className="animate-spin h-8 w-8 text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme-bg">
      <Navigation />
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Key className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-content-primary">Secrets Scanning</h1>
              {config ? (
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {config.gitHubScanEnabled && config.gitHubOrgName && (
                    <>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-gray-800 text-white">GitHub</span>
                      <span className="text-sm text-content-secondary font-mono">github.com/{config.gitHubOrgName}</span>
                    </>
                  )}
                  {config.gitLabScanEnabled && config.gitLabGroupName && (
                    <>
                      {config.gitHubScanEnabled && config.gitHubOrgName && <span className="text-content-secondary">·</span>}
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-orange-500 text-white">GitLab</span>
                      <span className="text-sm text-content-secondary font-mono truncate max-w-xs">
                        {config.gitLabBaseUrl ? `${config.gitLabBaseUrl}/${config.gitLabGroupName}` : `gitlab.com/${config.gitLabGroupName}`}
                      </span>
                    </>
                  )}
                  {!config.gitHubScanEnabled && !config.gitLabScanEnabled && (
                    <span className="text-sm text-content-secondary">No source configured</span>
                  )}
                </div>
              ) : (
                <p className="text-sm text-content-secondary">Detect leaked API keys and credentials in source code repositories</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <>
                <Button variant="secondary" onClick={() => navigate("/ConnectorsConfig?tab=secrets")} className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Configure
                </Button>
                {scanning ? (
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1.5 text-sm text-content-secondary">
                      <Loader className="animate-spin h-4 w-4" /> Scanning…
                    </span>
                    <Button variant="secondary" onClick={handleDismissScan} className="flex items-center gap-2 text-xs">
                      Stop waiting
                    </Button>
                  </div>
                ) : (
                  <Button onClick={handleTriggerScan} disabled={!config} className="flex items-center gap-2">
                    <RefreshCw className="h-4 w-4" /> Scan Now
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* No config state */}
        {!config && (
          <Card className="mb-6 text-center py-12">
            <Key className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-content-primary mb-2">No scan target configured</h3>
            <p className="text-sm text-content-secondary mb-4">
              Connect a GitHub org or GitLab group to start detecting leaked secrets in your source code.
            </p>
            {isAdmin && (
              <Button onClick={() => navigate("/ConnectorsConfig?tab=secrets")} className="inline-flex items-center gap-2">
                <Settings className="h-4 w-4" /> Configure Now
              </Button>
            )}
          </Card>
        )}

        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <SummaryCard label="Critical"   value={summary.critical}   textColor="text-red-600"    bgLight="bg-red-50" />
            <SummaryCard label="High"       value={summary.high}       textColor="text-red-500"    bgLight="bg-red-50" />
            <SummaryCard label="Medium"     value={summary.medium}     textColor="text-orange-500" bgLight="bg-orange-50" />
            <SummaryCard label="Total Open" value={summary.totalOpen}  textColor="text-purple-600" bgLight="bg-purple-50" />
          </div>
        )}

        {/* Scan submitted but completion not detected — give the user a manual refresh */}
        {scanTimedOut && (
          <div className="mb-4 flex items-center justify-between gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <span className="text-sm text-blue-800">
              Scan was submitted. Results may still be processing — click Refresh to reload.
            </span>
            <button
              onClick={() => { setScanTimedOut(false); loadData(); }}
              className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-blue-400 text-blue-700 bg-white hover:bg-blue-50"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
          </div>
        )}

        {/* Stats bar */}
        {summary && (
          <div className="flex flex-wrap items-center gap-4 text-xs text-content-secondary mb-4">
            {summary.lastScannedAt && <span>Last scanned: {timeAgo(summary.lastScannedAt)}</span>}
            <span>Acknowledged: {summary.totalAcknowledged}</span>
            <span>Resolved: {summary.totalResolved}</span>
            <span>False Positive: {summary.totalFalsePositive}</span>
          </div>
        )}

        {/* Filters */}
        {config && (
          <div className="flex flex-wrap gap-3 mb-4">
            <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}
              className="px-3 py-1.5 text-sm border border-theme-border rounded-md bg-theme-input text-content-primary focus:outline-none focus:ring-1 focus:ring-purple-500">
              <option value="">All Severities</option>
              {["Critical", "High", "Medium", "Low"].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="px-3 py-1.5 text-sm border border-theme-border rounded-md bg-theme-input text-content-primary focus:outline-none focus:ring-1 focus:ring-purple-500">
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
            <select value={filterSecretType} onChange={e => setFilterSecretType(e.target.value)}
              className="px-3 py-1.5 text-sm border border-theme-border rounded-md bg-theme-input text-content-primary focus:outline-none focus:ring-1 focus:ring-purple-500">
              <option value="">All Secret Types</option>
              {secretTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}

        {/* Empty state */}
        {config && findings.length === 0 && !loading && (
          <Card className="text-center py-10">
            <CheckCircle className="h-10 w-10 text-green-400 mx-auto mb-2" />
            <p className="text-content-secondary text-sm">
              {filterSeverity || filterStatus || filterSecretType
                ? "No findings match the selected filters."
                : "No findings detected. Run a scan to check for leaked secrets."}
            </p>
          </Card>
        )}

        {/* Findings + inline risks */}
        {findings.length > 0 && (
          <div className="space-y-3">
            {findings.map(f => (
              <SecretFindingCard
                key={f.id}
                finding={f}
                risk={risksByFindingId[f.id] || null}
                isAdmin={isAdmin}
                onStatusChange={handleStatusChange}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SecretFindingCard({ finding, risk, isAdmin, onStatusChange }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const severity    = SEVERITY_COLORS[finding.severity] || SEVERITY_COLORS.High;
  const hasRisk     = risk && risk.status !== "deleted";
  const isRiskClosed = hasRisk && risk.status === "closed";
  const ratingStyle = hasRisk ? (RISK_RATING_STYLE[risk.inherentRiskRating?.toUpperCase()] || RISK_RATING_STYLE.MEDIUM) : null;

  const ageLabel   = timeAgo(finding.firstDetectedAt);
  const stillToday = isToday(finding.lastSeenAt);
  // Use stored URL (GitHub, GitLab or Azure DevOps); fall back to constructed GitHub URL for pre-multi-provider findings
  const repoUrl      = finding.repoUrl || (finding.repoFullName ? `https://github.com/${finding.repoFullName}` : null);
  const repoProvider = repoProviderFor(repoUrl);

  return (
    <Card className="p-0 overflow-hidden">
      {/* ── Collapsed header ─────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-theme-hover"
        onClick={() => setExpanded(p => !p)}
      >
        <div className="flex-shrink-0">
          {expanded
            ? <ChevronDown className="h-4 w-4 text-content-secondary" />
            : <ChevronRight className="h-4 w-4 text-content-secondary" />
          }
        </div>

        <span className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${severity.badge}`}>
          {finding.severity}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-content-primary">{finding.secretType}</span>
            <code className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded font-mono text-gray-700 dark:text-gray-300">
              {finding.maskedValue}
            </code>
            {finding.aiValidated && (
              <span title="AI validated" className="text-purple-500 flex items-center gap-0.5 text-xs">
                <Shield className="h-3 w-3" /> AI validated
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs font-mono text-content-secondary truncate max-w-xs">{finding.repoFullName}</span>
            {repoProvider && (
              <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold tracking-wide text-white ${REPO_PROVIDER_BADGE[repoProvider] || "bg-gray-500"}`}>
                {repoProvider}
              </span>
            )}
            <span className="text-[10px] text-content-secondary">·</span>
            <span className="text-[10px] text-content-secondary">
              Found {ageLabel}
              {stillToday
                ? <span className="ml-1 text-orange-500 font-medium">· still present today</span>
                : <span className="ml-1 text-content-secondary">· last seen {timeAgo(finding.lastSeenAt)}</span>}
            </span>
          </div>
        </div>

        {/* Business risk badge */}
        {hasRisk ? (
          <div className="flex-shrink-0 flex items-center gap-1.5">
            <Zap className={`h-3.5 w-3.5 ${isRiskClosed ? "text-green-500" : "text-purple-500"}`} />
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isRiskClosed ? "bg-green-50 text-green-700 border border-green-200" : ratingStyle.pill}`}>
              {isRiskClosed ? "Closed" : `${risk.inherentRiskRating} Risk`}
            </span>
          </div>
        ) : (
          <span className="flex-shrink-0 text-xs text-content-secondary italic">No risk yet</span>
        )}

        {/* Fix owner (collapsed, large screens) */}
        {hasRisk && risk.fixOwner && (
          <span className="flex-shrink-0 hidden lg:flex items-center gap-1 text-xs text-content-secondary">
            <User className="h-3 w-3" /> {risk.fixOwner}
          </span>
        )}

        {/* Confidence */}
        <span className="flex-shrink-0 hidden md:block text-xs text-content-secondary">
          {finding.confidenceScore}% conf
        </span>

        {/* Status badge */}
        <span className="flex-shrink-0 text-xs text-content-secondary px-2 py-0.5 rounded border border-theme-border bg-theme-surface">
          {STATUS_LABEL[finding.status] || finding.status}
        </span>
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div className="border-t border-theme-border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-theme-border">

            {/* Left: Technical details */}
            <div className="px-5 py-4 space-y-3 bg-theme-surface">
              <p className="text-xs font-semibold text-content-secondary uppercase tracking-wider">Technical Finding</p>

              <div className="space-y-1 text-xs text-content-secondary">
                <div><span className="font-medium text-content-primary">Repository: </span>{finding.repoFullName}</div>
                <div><span className="font-medium text-content-primary">File: </span>
                  <code className="font-mono">{finding.filePath}</code> line {finding.lineNumber}
                </div>
                <div><span className="font-medium text-content-primary">Confidence: </span>{finding.confidenceScore}%</div>
              </div>

              {finding.remediationGuidance && (
                <div className="flex gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-xs font-medium text-content-primary">Technical Remediation: </span>
                    <span className="text-xs text-content-secondary">{finding.remediationGuidance}</span>
                  </div>
                </div>
              )}

              {finding.aiReason && (
                <div className="flex gap-2">
                  <Info className="h-4 w-4 text-purple-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-xs font-medium text-content-primary">AI Analysis: </span>
                    <span className="text-xs text-content-secondary">{finding.aiReason}</span>
                  </div>
                </div>
              )}

              {repoUrl && (
                <a
                  href={repoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-blue-500 hover:underline"
                  onClick={e => e.stopPropagation()}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  View on {repoProvider}
                </a>
              )}

              {/* Action buttons */}
              {isAdmin && (
                <div className="pt-2 flex flex-wrap gap-2 border-t border-theme-border">
                  {finding.status !== "Resolved" && (
                    <button
                      onClick={e => { e.stopPropagation(); onStatusChange(finding.id, "Resolved"); }}
                      className="px-3 py-1 text-xs rounded border border-green-300 text-green-700 hover:bg-green-50 font-medium"
                    >
                      ✓ Mark Resolved
                    </button>
                  )}
                  {finding.status !== "Acknowledged" && (
                    <button
                      onClick={e => { e.stopPropagation(); onStatusChange(finding.id, "Acknowledged"); }}
                      className="px-3 py-1 text-xs rounded border border-blue-300 text-blue-700 hover:bg-blue-50 font-medium"
                    >
                      Acknowledge
                    </button>
                  )}
                  {finding.status !== "FalsePositive" && (
                    <button
                      onClick={e => { e.stopPropagation(); onStatusChange(finding.id, "FalsePositive"); }}
                      className="px-3 py-1 text-xs rounded border border-theme-border text-content-secondary hover:bg-theme-hover"
                    >
                      False Positive
                    </button>
                  )}
                  {finding.status !== "Open" && (
                    <button
                      onClick={e => { e.stopPropagation(); onStatusChange(finding.id, "Open"); }}
                      className="px-3 py-1 text-xs rounded border border-theme-border text-content-secondary hover:bg-theme-hover"
                    >
                      Reopen
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Right: Business risk */}
            {hasRisk ? (
              <div className={`border-l-4 ${isRiskClosed ? "border-l-green-500" : ratingStyle.accent} bg-theme-surface`}>
                {/* Risk header */}
                <div className="px-5 pt-4 pb-3 border-b border-theme-border">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-content-secondary uppercase tracking-wider flex items-center gap-1.5">
                      <TrendingUp className="h-3.5 w-3.5 text-purple-500" />
                      Business Risk
                    </span>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded border ${ratingStyle.pill}`}>
                        {risk.inherentRiskRating}
                      </span>
                      <span className="text-xs text-content-secondary">{RISK_STATUS_LABEL[risk.status] || risk.status}</span>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-content-primary leading-snug">{risk.title}</p>
                </div>

                <div className="px-5 py-3 space-y-3">
                  {/* Business impact */}
                  {risk.businessImpact && (
                    <div>
                      <p className="text-xs font-semibold text-content-secondary uppercase tracking-wider mb-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3 text-orange-500" /> Business Impact
                      </p>
                      <p className="text-xs text-content-primary leading-relaxed">{risk.businessImpact}</p>
                    </div>
                  )}

                  {/* What may break */}
                  {risk.whatMayBreak && (
                    <div>
                      <p className="text-xs font-semibold text-content-secondary uppercase tracking-wider mb-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3 text-red-500" /> What May Break
                      </p>
                      <p className="text-xs text-content-primary leading-relaxed whitespace-pre-line">{risk.whatMayBreak}</p>
                    </div>
                  )}

                  {/* Remediation steps */}
                  {risk.remediationSteps && (
                    <div>
                      <p className="text-xs font-semibold text-content-secondary uppercase tracking-wider mb-1 flex items-center gap-1">
                        <Wrench className="h-3 w-3 text-blue-500" /> Remediation Steps
                      </p>
                      <p className="text-xs text-content-primary leading-relaxed whitespace-pre-line">{risk.remediationSteps}</p>
                    </div>
                  )}

                  {/* Footer meta */}
                  <div className="flex flex-wrap items-center gap-3 pt-1 text-xs text-content-secondary border-t border-theme-border">
                    {risk.fixOwner && (
                      <span className="flex items-center gap-1 font-medium text-content-primary">
                        <User className="h-3 w-3" /> {risk.fixOwner}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Identified {timeAgo(risk.createdAt)}
                    </span>
                    <span>Category: {risk.category}</span>
                    {risk.grcRiskId && (
                      <button
                        onClick={e => { e.stopPropagation(); navigate(`/risks?riskId=${risk.grcRiskId}`); }}
                        className="ml-auto flex items-center gap-1.5 px-2.5 py-1 border rounded-lg font-medium transition-all bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 active:scale-95"
                      >
                        <ExternalLink className="h-3 w-3" />
                        View Risk
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="px-5 py-4 flex flex-col items-center justify-center text-center text-xs text-content-secondary gap-2 bg-theme-surface">
                <Shield className="h-6 w-6 text-content-secondary opacity-30" />
                <span>Business risk will appear after the next scan completes.</span>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function SummaryCard({ label, value, textColor, bgLight }) {
  return (
    <Card className={`${bgLight} border-0`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-content-secondary font-medium uppercase tracking-wide">{label}</p>
          <p className={`text-3xl font-bold mt-1 ${textColor}`}>{value ?? 0}</p>
        </div>
      </div>
    </Card>
  );
}
